import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Grid,
  Stack,
  Autocomplete,
  Checkbox,
  InputAdornment
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Description as InvoiceIcon,
  ShoppingCart as PurchaseIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
  Edit as EditIcon,
  FindInPage as FindInPageIcon,
  AutoFixHigh as ReanalyzeIcon
} from '@mui/icons-material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { api } from '../contexts/ApiContext';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  fitTruncateSx,
  fitChipSx,
} from '../utils/fitTable';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`receipts-invoices-tabpanel-${index}`}
      aria-labelledby={`receipts-invoices-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TAB_KEYS = ['purchase-invoices', 'receipts', 'sales-invoices'] as const;

const tabIndexFromParam = (tabParam: string | null): number => {
  if (!tabParam) return 0;
  const named = TAB_KEYS.indexOf(tabParam.toLowerCase() as (typeof TAB_KEYS)[number]);
  if (named >= 0) return named;
  const asNumber = parseInt(tabParam, 10);
  if (!Number.isNaN(asNumber) && asNumber >= 0 && asNumber < TAB_KEYS.length) {
    return asNumber;
  }
  return 0;
};

const fileEndpointFor = (type: string, uid: string): string => {
  if (type === 'receipt') return `/api/receipts/${uid}/file`;
  if (type === 'sales-invoice') return `/api/sales-invoices/${uid}/file`;
  if (type === 'purchase-invoice') return `/api/purchase-invoices/${uid}/file`;
  return '';
};

const guessPreviewMime = (
  mimeType?: string | null,
  fileExtension?: string | null,
  fileName?: string | null
): string => {
  const raw = (mimeType || '').trim().toLowerCase();
  if (raw && raw !== 'application/octet-stream' && raw !== 'binary/octet-stream') {
    return raw;
  }
  let ext = (fileExtension || '').trim().toLowerCase().replace(/^\./, '');
  if (!ext && fileName && fileName.includes('.')) {
    ext = fileName.split('.').pop()!.toLowerCase();
  }
  const byExt: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp',
    tif: 'image/tiff',
    tiff: 'image/tiff',
  };
  return byExt[ext] || raw || 'application/octet-stream';
};

const isPdfPreview = (mime: string, fileExtension?: string | null, fileName?: string | null) => {
  const ext = (fileExtension || fileName?.split('.').pop() || '').toLowerCase().replace(/^\./, '');
  return mime.includes('pdf') || ext === 'pdf';
};

const isImagePreview = (mime: string, fileExtension?: string | null, fileName?: string | null) => {
  const ext = (fileExtension || fileName?.split('.').pop() || '').toLowerCase().replace(/^\./, '');
  return mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'tif', 'tiff'].includes(ext);
};

interface ViewMeta {
  type: string;
  uid: string;
  fileName?: string;
  mimeType?: string | null;
  fileExtension?: string | null;
  localFile?: File;
}

interface CanonicalSupplier {
  id: number;
  name: string;
}

interface Receipt {
  id?: number | null;
  uid: string;
  user_id: number;
  revolut_receipt_id: string | null;
  file_name: string;
  file_path: string;
  file_extension: string;
  file_size_bytes: number | null;
  mime_type: string | null;
  description: string | null;
  receipt_date: string | null;
  supplier_id?: number | null;
  supplier_name?: string | null;
  created_at: string;
  updated_at: string;
  linked_transaction_ids?: number[];
}

interface SalesInvoice {
  id?: number | null;
  uid: string;
  user_id: number;
  invoice_number: string | null;
  file_name: string;
  file_path: string;
  file_extension: string;
  file_size_bytes: number | null;
  mime_type: string | null;
  customer_name: string | null;
  customer_email: string | null;
  invoice_date: string | null;
  due_date: string | null;
  amount: number | null;
  currency: string;
  tax_amount: number | null;
  total_amount: number | null;
  status: string;
  paid_date: string | null;
  payment_reference: string | null;
  description: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  linked_transaction_ids?: number[];
}

interface PurchaseInvoice {
  id?: number | null;
  uid: string;
  user_id: number;
  invoice_number: string | null;
  supplier_name: string | null;
  supplier_email: string | null;
  file_name: string;
  file_path: string;
  file_extension: string;
  file_size_bytes: number | null;
  mime_type: string | null;
  invoice_date: string | null;
  due_date: string | null;
  amount: number | null;
  currency: string;
  tax_amount: number | null;
  total_amount: number | null;
  status: string;
  approved_date: string | null;
  paid_date: string | null;
  payment_reference: string | null;
  description: string | null;
  notes: string | null;
  category: string | null;
  created_at: string;
  updated_at: string;
  linked_transaction_ids?: number[];
  linked_transactions?: LinkedTxSummary[];
}

interface UnifiedDocument {
  uid: string;
  file_name: string;
  created_at: string;
  document_type: 'receipt' | 'sales_invoice' | 'purchase_invoice';
}

interface LinkableTransaction {
  id: number;
  revolut_id: string;
  description: string | null;
  amount: number | null;
  currency: string | null;
  account_source: string;
  completed_at: string | null;
  bank_account_name: string | null;
  linked_documents_count: number;
}

type DocumentTypeChoice = 'receipt' | 'sales_invoice' | 'purchase_invoice';
type PredictStatus = 'idle' | 'pending' | 'done' | 'error';

interface DocumentMetadataDraft {
  documentType: DocumentTypeChoice;
  description: string;
  invoiceNumber: string;
  invoiceDate: string;
  supplierName: string;
  supplierId: number | null;
  receiptDate: string;
  amount: string;
  currency: string;
  dueDate: string;
}

interface LinkedTxSummary {
  id: number;
  revolut_id?: string | null;
  amount: number | null;
  currency?: string | null;
  completed_at?: string | null;
  description?: string | null;
}

interface TxMatchCandidate {
  id: number;
  revolut_id: string;
  description: string | null;
  merchant_name?: string | null;
  amount: number | null;
  currency: string | null;
  account_source?: string | null;
  completed_at: string | null;
  bank_account_name: string | null;
  amount_match: boolean;
  supplier_match: boolean;
  matched_supplier_as?: string | null;
  direction_match: boolean;
  date_distance_days: number | null;
  reasons: string[];
}

interface TxMatchPayload {
  document_type: string;
  document_uid: string;
  amount: number | null;
  currency: string | null;
  party_name: string | null;
  invoice_date: string | null;
  direction: string;
  candidates: TxMatchCandidate[];
  error?: string;
  linked_transactions?: LinkedTxSummary[];
  file_name?: string | null;
  mime_type?: string | null;
  file_extension?: string | null;
}

interface TxMatchDoc {
  type: 'purchase_invoice' | 'sales_invoice';
  uid: string;
  label: string;
  fileName?: string | null;
  mimeType?: string | null;
  fileExtension?: string | null;
}

interface PurchaseBulkDraft {
  supplierName: string;
  supplierId: number | null;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  amount: string;
  currency: string;
  category: string;
  status: string;
  description: string;
}

const emptyPurchaseBulkDraft = (): PurchaseBulkDraft => ({
  supplierName: '',
  supplierId: null,
  invoiceNumber: '',
  invoiceDate: '',
  dueDate: '',
  amount: '',
  currency: '',
  category: '',
  status: '',
  description: '',
});

const purchaseBulkFieldsPayload = (draft: PurchaseBulkDraft): Record<string, string> => {
  const fields: Record<string, string> = {};
  if (draft.supplierName.trim()) fields.supplier_name = draft.supplierName.trim();
  if (draft.invoiceNumber.trim()) fields.invoice_number = draft.invoiceNumber.trim();
  if (draft.invoiceDate.trim()) fields.invoice_date = draft.invoiceDate.trim();
  if (draft.dueDate.trim()) fields.due_date = draft.dueDate.trim();
  if (draft.amount.trim()) {
    fields.amount = draft.amount.trim();
    fields.total_amount = draft.amount.trim();
  }
  if (draft.currency.trim()) fields.currency = draft.currency.trim().toUpperCase();
  if (draft.category.trim()) fields.category = draft.category.trim();
  if (draft.status.trim()) fields.status = draft.status.trim();
  if (draft.description.trim()) fields.description = draft.description.trim();
  return fields;
};

interface PendingUpload extends DocumentMetadataDraft {
  localId: string;
  file: File;
  predictStatus: PredictStatus;
}

const documentTypeLabel = (type: DocumentTypeChoice): string => {
  if (type === 'sales_invoice') return 'Sales Invoice';
  if (type === 'purchase_invoice') return 'Purchase Invoice';
  return 'Receipt';
};

const uploadActionLabel = (type: DocumentTypeChoice): string => {
  if (type === 'sales_invoice') return 'Upload Sales Invoice(s)';
  if (type === 'purchase_invoice') return 'Upload Purchase Invoice(s)';
  return 'Upload Receipt(s)';
};

const emptyMetadataDraft = (documentType: DocumentTypeChoice): DocumentMetadataDraft => ({
  documentType,
  description: '',
  invoiceNumber: '',
  invoiceDate: '',
  supplierName: '',
  supplierId: null,
  receiptDate: '',
  amount: '',
  currency: '',
  dueDate: '',
});

const formatExtractedAmount = (value: unknown): string => {
  if (value == null || value === '') return '';
  const asNumber = Number(value);
  if (Number.isFinite(asNumber)) return asNumber.toFixed(2);
  return String(value).trim();
};

const draftCurrencyCode = (currency: string): string => (currency || 'EUR').trim().toUpperCase() || 'EUR';

const draftCurrencySymbol = (currency: string): string => {
  try {
    const parts = new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: draftCurrencyCode(currency),
    }).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value || draftCurrencyCode(currency);
  } catch {
    return draftCurrencyCode(currency);
  }
};

const formatDraftAmountAsCurrency = (amount: string, currency: string): string => {
  const trimmed = amount.trim().replace(/,/g, '');
  if (!trimmed) return '—';
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return amount.trim();
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: draftCurrencyCode(currency),
    }).format(parsed);
  } catch {
    return `${parsed.toFixed(2)} ${draftCurrencyCode(currency)}`;
  }
};

const calendarDaysUntilDue = (dueDate: string | null | undefined): number | null => {
  if (!dueDate) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dueDate.trim());
  if (!match) return null;
  const due = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((due.getTime() - today.getTime()) / 86400000);
};

const duePayStatus = (
  dueDate: string | null | undefined,
): { label: string; color: 'default' | 'success' | 'warning' | 'error' } | null => {
  const days = calendarDaysUntilDue(dueDate);
  if (days == null) return null;
  if (days > 7) return { label: `${days} days left`, color: 'success' };
  if (days > 1) return { label: `${days} days left`, color: 'warning' };
  if (days === 1) return { label: '1 day left', color: 'warning' };
  if (days === 0) return { label: 'Due today', color: 'warning' };
  const overdue = Math.abs(days);
  return { label: overdue === 1 ? '1 day overdue' : `${overdue} days overdue`, color: 'error' };
};

const DuePayCell: React.FC<{ dueDate: string | null | undefined }> = ({ dueDate }) => {
  const status = duePayStatus(dueDate);
  if (!status) return <>—</>;
  return <Chip size="small" sx={fitChipSx} color={status.color} label={status.label} />;
};

const newPendingUpload = (file: File, documentType: DocumentTypeChoice): PendingUpload => ({
  localId: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
  file,
  ...emptyMetadataDraft(documentType),
  predictStatus: 'idle',
});

const pendingMetadataFields = (item: PendingUpload): Array<{ label: string; value: string }> => {
  const value = (raw: string) => raw.trim() || '—';
  if (item.documentType === 'receipt') {
    return [
      { label: 'Supplier', value: value(item.supplierName) },
      { label: 'Receipt Date', value: value(item.receiptDate) },
      { label: 'Description', value: value(item.description) },
    ];
  }
  if (item.documentType === 'sales_invoice') {
    return [
      { label: 'Invoice #', value: value(item.invoiceNumber) },
      { label: 'Invoice Date', value: value(item.invoiceDate) },
      { label: 'Due Date', value: value(item.dueDate) },
      { label: 'Amount', value: formatDraftAmountAsCurrency(item.amount, item.currency) },
      { label: 'Description', value: value(item.description) },
    ];
  }
  return [
    { label: 'Supplier', value: value(item.supplierName) },
    { label: 'Invoice #', value: value(item.invoiceNumber) },
    { label: 'Invoice Date', value: value(item.invoiceDate) },
    { label: 'Due Date', value: value(item.dueDate) },
    { label: 'Amount', value: formatDraftAmountAsCurrency(item.amount, item.currency) },
    { label: 'Description', value: value(item.description) },
  ];
};

const pendingHasMetadata = (item: PendingUpload): boolean =>
  pendingMetadataFields(item).some((row) => row.value !== '—');

const pendingStatusChip = (item: PendingUpload): { color: 'default' | 'info' | 'success' | 'warning'; label: string } => {
  if (item.predictStatus === 'pending') return { color: 'info', label: 'Reading…' };
  if (item.predictStatus === 'error') return { color: 'warning', label: "Couldn't read" };
  if (pendingHasMetadata(item)) {
    return { color: 'success', label: item.predictStatus === 'done' ? 'Suggested' : 'Has details' };
  }
  return { color: 'default', label: 'No details' };
};

const nonEmptyMetadataPatch = (draft: DocumentMetadataDraft): Partial<PendingUpload> => {
  const patch: Partial<PendingUpload> = { documentType: draft.documentType };
  if (draft.description.trim()) patch.description = draft.description.trim();
  if (draft.invoiceNumber.trim()) patch.invoiceNumber = draft.invoiceNumber.trim();
  if (draft.invoiceDate.trim()) patch.invoiceDate = draft.invoiceDate.trim();
  if (draft.supplierId != null) {
    patch.supplierId = draft.supplierId;
    patch.supplierName = draft.supplierName;
  } else if (draft.supplierName.trim()) {
    patch.supplierName = draft.supplierName.trim();
  }
  if (draft.receiptDate.trim()) patch.receiptDate = draft.receiptDate.trim();
  if (draft.amount.trim()) patch.amount = draft.amount.trim();
  if (draft.currency.trim()) patch.currency = draft.currency.trim().toUpperCase();
  if (draft.dueDate.trim()) patch.dueDate = draft.dueDate.trim();
  return patch;
};

const DocumentMetadataFields: React.FC<{
  item: DocumentMetadataDraft;
  onChange: (patch: Partial<DocumentMetadataDraft>) => void;
  showType?: boolean;
  suppliers: CanonicalSupplier[];
}> = ({ item, onChange, showType = true, suppliers }) => {
  const selectedSupplier =
    suppliers.find((supplier) => supplier.id === item.supplierId)
    || suppliers.find((supplier) => supplier.name === item.supplierName)
    || null;
  return (
  <Stack spacing={2}>
    {showType && (
      <FormControl fullWidth>
        <InputLabel>Document Type</InputLabel>
        <Select
          label="Document Type"
          value={item.documentType}
          onChange={(e) => onChange({ documentType: e.target.value as DocumentTypeChoice })}
        >
          <MenuItem value="receipt">Receipt</MenuItem>
          <MenuItem value="sales_invoice">Sales Invoice</MenuItem>
          <MenuItem value="purchase_invoice">Purchase Invoice</MenuItem>
        </Select>
      </FormControl>
    )}
    <TextField
      label="Description"
      value={item.description}
      onChange={(e) => onChange({ description: e.target.value })}
      fullWidth
    />
    {item.documentType === 'receipt' && (
      <TextField
        label="Receipt Date (YYYY-MM-DD)"
        value={item.receiptDate}
        onChange={(e) => onChange({ receiptDate: e.target.value })}
        fullWidth
      />
    )}
    {(item.documentType === 'receipt' || item.documentType === 'purchase_invoice') && (
      <Autocomplete
        options={suppliers}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={selectedSupplier}
        onChange={(_, value) => onChange({
          supplierId: value?.id ?? null,
          supplierName: value?.name ?? '',
        })}
        autoHighlight
        autoComplete
        includeInputInList
        fullWidth
        slotProps={{
          popper: {
            sx: { zIndex: (theme) => theme.zIndex.modal + 10 },
          },
        }}
        ListboxProps={{ sx: { maxHeight: 280 } }}
        noOptionsText={suppliers.length === 0 ? 'No suppliers loaded' : 'No matching suppliers'}
        renderInput={(params) => <TextField {...params} label="Supplier" />}
      />
    )}
    {item.documentType !== 'receipt' && (
      <>
        <TextField
          label="Invoice Number"
          value={item.invoiceNumber}
          onChange={(e) => onChange({ invoiceNumber: e.target.value })}
          fullWidth
        />
        <TextField
          label="Invoice Date (YYYY-MM-DD)"
          value={item.invoiceDate}
          onChange={(e) => onChange({ invoiceDate: e.target.value })}
          fullWidth
        />
        <TextField
          label="Due Date (YYYY-MM-DD)"
          value={item.dueDate}
          onChange={(e) => onChange({ dueDate: e.target.value })}
          fullWidth
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Amount"
            value={item.amount}
            onChange={(e) => onChange({ amount: e.target.value })}
            fullWidth
            placeholder="0.00"
            inputProps={{ inputMode: 'decimal' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">{draftCurrencySymbol(item.currency)}</InputAdornment>
              ),
            }}
          />
          <TextField
            label="Currency"
            value={item.currency}
            onChange={(e) => onChange({ currency: e.target.value.toUpperCase() })}
            fullWidth
            inputProps={{ maxLength: 3 }}
          />
        </Stack>
      </>
    )}
  </Stack>
);
};

const ReceiptsInvoices: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabValue = tabIndexFromParam(searchParams.get('tab'));
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [salesInvoices, setSalesInvoices] = useState<SalesInvoice[]>([]);
  const [purchaseInvoices, setPurchaseInvoices] = useState<PurchaseInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ type: string; uid: string; name: string } | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewMeta, setViewMeta] = useState<ViewMeta | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [txTabTransactions, setTxTabTransactions] = useState<LinkableTransaction[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<UnifiedDocument | null>(null);
  const [documentTypeInput, setDocumentTypeInput] = useState<DocumentTypeChoice>('receipt');
  const documentTypeInputRef = useRef<DocumentTypeChoice>('receipt');
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [editReceipt, setEditReceipt] = useState<Receipt | null>(null);
  const [editReceiptDescription, setEditReceiptDescription] = useState('');
  const [editReceiptDate, setEditReceiptDate] = useState('');
  const [editReceiptSupplierId, setEditReceiptSupplierId] = useState<number | null>(null);
  const [canonicalSuppliers, setCanonicalSuppliers] = useState<CanonicalSupplier[]>([]);
  const [selectedLocalIds, setSelectedLocalIds] = useState<string[]>([]);
  const [metadataEditIds, setMetadataEditIds] = useState<string[]>([]);
  const [bulkDraft, setBulkDraft] = useState<DocumentMetadataDraft | null>(null);
  const [matchDoc, setMatchDoc] = useState<TxMatchDoc | null>(null);
  const [matchPayload, setMatchPayload] = useState<TxMatchPayload | null>(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchError, setMatchError] = useState<string | null>(null);
  const [matchAcceptingId, setMatchAcceptingId] = useState<number | null>(null);
  const [rejectedTxIds, setRejectedTxIds] = useState<number[]>([]);
  const [matchAmountInput, setMatchAmountInput] = useState('');
  const [matchCurrencyInput, setMatchCurrencyInput] = useState('EUR');
  const [matchQueueIndex, setMatchQueueIndex] = useState(0);
  const [matchQueueLength, setMatchQueueLength] = useState(0);
  const matchQueueRef = useRef<TxMatchDoc[]>([]);
  const matchQueueIndexRef = useRef(0);
  const matchIncludeAmountOnlyRef = useRef(false);
  const matchOcrAttemptedRef = useRef<Record<string, boolean>>({});
  const matchPreviewUrlRef = useRef<string | null>(null);
  const [pandleReviewBusy, setPandleReviewBusy] = useState(false);
  const [matchPreviewUrl, setMatchPreviewUrl] = useState<string | null>(null);
  const [matchPreviewLoading, setMatchPreviewLoading] = useState(false);
  const [matchPreviewError, setMatchPreviewError] = useState<string | null>(null);
  const [matchOcrBusy, setMatchOcrBusy] = useState(false);
  const [purchaseBulkDraft, setPurchaseBulkDraft] = useState<PurchaseBulkDraft | null>(null);
  const [selectedPurchaseUids, setSelectedPurchaseUids] = useState<string[]>([]);
  const [purchaseBulkDeleteOpen, setPurchaseBulkDeleteOpen] = useState(false);
  const [purchaseBulkBusy, setPurchaseBulkBusy] = useState(false);
  const [reanalyzeUid, setReanalyzeUid] = useState<string | null>(null);
  const [purchaseReanalyzeProgress, setPurchaseReanalyzeProgress] = useState<string | null>(null);
  const extractQueueRef = useRef<string[]>([]);
  const extractBusyRef = useRef(false);
  const canonicalSupplierNamesRef = useRef<string[]>([]);
  const pendingUploadsRef = useRef<PendingUpload[]>([]);
  canonicalSupplierNamesRef.current = canonicalSuppliers.map((supplier) => supplier.name);
  pendingUploadsRef.current = pendingUploads;

  const fetchReceipts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/receipts');
      setReceipts(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch receipts');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/sales-invoices');
      setSalesInvoices(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch sales invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/purchase-invoices');
      setPurchaseInvoices(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch purchase invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const valid = new Set(purchaseInvoices.map((invoice) => invoice.uid));
    setSelectedPurchaseUids((prev) => prev.filter((uid) => valid.has(uid)));
  }, [purchaseInvoices]);

  const fetchTxFirstTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/document-linkable-transactions');
      setTxTabTransactions(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadSuppliers = async () => {
      try {
        const response = await api.get('/api/suppliers/', { params: { is_active: 'true' } });
        if (cancelled) return;
        const rows = Array.isArray(response.data?.suppliers)
          ? response.data.suppliers
          : (Array.isArray(response.data) ? response.data : []);
        const loaded: CanonicalSupplier[] = rows
          .map((supplier: { id?: unknown; name?: string }) => ({
            id: Number(supplier.id),
            name: String(supplier?.name || '').trim(),
          }))
          .filter((supplier: CanonicalSupplier) => Number.isFinite(supplier.id) && supplier.id > 0 && supplier.name)
          .sort((a: CanonicalSupplier, b: CanonicalSupplier) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        const unique = new Map<number, CanonicalSupplier>();
        loaded.forEach((supplier) => unique.set(supplier.id, supplier));
        setCanonicalSuppliers(Array.from(unique.values()));
      } catch {
        if (!cancelled) setCanonicalSuppliers([]);
      }
    };
    loadSuppliers();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const resolvedKey = TAB_KEYS[tabIndexFromParam(searchParams.get('tab'))] || TAB_KEYS[0];
    if ((searchParams.get('tab') || '').toLowerCase() === resolvedKey) {
      return;
    }
    setSearchParams((prev) => {
      const nextKey = TAB_KEYS[tabIndexFromParam(prev.get('tab'))] || TAB_KEYS[0];
      if ((prev.get('tab') || '').toLowerCase() === nextKey) {
        return prev;
      }
      const nextParams = new URLSearchParams(prev);
      nextParams.set('tab', nextKey);
      return nextParams;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const key = TAB_KEYS[tabValue];
    if (key === 'purchase-invoices') {
      fetchPurchaseInvoices();
    } else if (key === 'receipts') {
      fetchReceipts();
    } else if (key === 'sales-invoices') {
      fetchSalesInvoices();
    }
  }, [tabValue]);

  useEffect(() => {
    if (!viewDialogOpen || !viewMeta) {
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    const loadPreview = async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      setPreviewUrl(null);
      try {
        if (viewMeta.localFile) {
          const file = viewMeta.localFile;
          const mime = guessPreviewMime(file.type, viewMeta.fileExtension, viewMeta.fileName);
          const blobData: Blob = file.type === mime ? file : new Blob([file], { type: mime });
          objectUrl = URL.createObjectURL(blobData);
          if (!cancelled) {
            setPreviewUrl(objectUrl);
          } else {
            URL.revokeObjectURL(objectUrl);
            objectUrl = null;
          }
          return;
        }
        const endpoint = fileEndpointFor(viewMeta.type, viewMeta.uid);
        const response = await api.get(endpoint, { responseType: 'blob', timeout: 180000 });
        const blobData: Blob = response.data;
        if (blobData.type && blobData.type.includes('application/json')) {
          const parsed = JSON.parse(await blobData.text());
          throw new Error(parsed.error || 'Failed to load document');
        }
        const mime = guessPreviewMime(viewMeta.mimeType || blobData.type, viewMeta.fileExtension, viewMeta.fileName);
        objectUrl = URL.createObjectURL(blobData.type === mime ? blobData : new Blob([blobData], { type: mime }));
        if (!cancelled) {
          setPreviewUrl(objectUrl);
        } else {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
      } catch (err: any) {
        let message = 'Failed to load document';
        const data = err.response?.data;
        if (data instanceof Blob) {
          try {
            const parsed = JSON.parse(await data.text());
            message = parsed.error || message;
          } catch {
            message = err.message || message;
          }
        } else {
          message = err.response?.data?.error || err.message || message;
        }
        if (!cancelled) {
          setPreviewError(message);
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    };
    loadPreview();
    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [viewDialogOpen, viewMeta]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    const loadMatchPreview = async () => {
      if (!matchDoc) {
        if (matchPreviewUrlRef.current) {
          URL.revokeObjectURL(matchPreviewUrlRef.current);
          matchPreviewUrlRef.current = null;
        }
        setMatchPreviewUrl(null);
        setMatchPreviewError(null);
        setMatchPreviewLoading(false);
        return;
      }
      setMatchPreviewLoading(true);
      setMatchPreviewError(null);
      try {
        const endpoint = fileEndpointFor(
          matchDoc.type === 'sales_invoice' ? 'sales-invoice' : 'purchase-invoice',
          matchDoc.uid
        );
        const response = await api.get(endpoint, { responseType: 'blob', timeout: 180000 });
        const blobData: Blob = response.data;
        if (blobData.type && blobData.type.includes('application/json')) {
          const parsed = JSON.parse(await blobData.text());
          throw new Error(parsed.error || 'Failed to load document');
        }
        const mime = guessPreviewMime(matchDoc.mimeType || blobData.type, matchDoc.fileExtension, matchDoc.fileName);
        objectUrl = URL.createObjectURL(blobData.type === mime ? blobData : new Blob([blobData], { type: mime }));
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
          return;
        }
        if (matchPreviewUrlRef.current) {
          URL.revokeObjectURL(matchPreviewUrlRef.current);
        }
        matchPreviewUrlRef.current = objectUrl;
        setMatchPreviewUrl(objectUrl);
      } catch (err: any) {
        if (!cancelled) {
          setMatchPreviewError(err.response?.data?.error || err.message || 'Failed to load document');
        }
      } finally {
        if (!cancelled) {
          setMatchPreviewLoading(false);
        }
      }
    };
    loadMatchPreview();
    return () => {
      cancelled = true;
    };
  }, [matchDoc]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const nextKey = TAB_KEYS[newValue] || TAB_KEYS[0];
    if ((searchParams.get('tab') || '').toLowerCase() === nextKey) {
      return;
    }
    setSelectedPurchaseUids([]);
    setPurchaseBulkDraft(null);
    setPurchaseBulkDeleteOpen(false);
    setSearchParams((prev) => {
      if ((prev.get('tab') || '').toLowerCase() === nextKey) {
        return prev;
      }
      const nextParams = new URLSearchParams(prev);
      nextParams.set('tab', nextKey);
      return nextParams;
    }, { replace: true });
  };

  const closeViewDialog = () => {
    setViewDialogOpen(false);
    setViewMeta(null);
    setPreviewUrl(null);
    setPreviewError(null);
    setPreviewLoading(false);
  };

  const handleDelete = (type: string, uid: string, name: string) => {
    setItemToDelete({ type, uid, name });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setLoading(true);
      let endpoint = '';
      if (itemToDelete.type === 'receipt') {
        endpoint = `/api/receipts/${itemToDelete.uid}`;
      } else if (itemToDelete.type === 'sales-invoice') {
        endpoint = `/api/sales-invoices/${itemToDelete.uid}`;
      } else if (itemToDelete.type === 'purchase-invoice') {
        endpoint = `/api/purchase-invoices/${itemToDelete.uid}`;
      }

      await api.delete(endpoint);
      
      const key = TAB_KEYS[tabValue];
      if (key === 'purchase-invoices') {
        fetchPurchaseInvoices();
      } else if (key === 'receipts') {
        fetchReceipts();
      } else if (key === 'sales-invoices') {
        fetchSalesInvoices();
      }

      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete item');
    } finally {
      setLoading(false);
    }
  };

  const allPurchaseSelected = purchaseInvoices.length > 0
    && selectedPurchaseUids.length === purchaseInvoices.length;

  const togglePurchaseSelected = (uid: string) => {
    setSelectedPurchaseUids((prev) => (
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    ));
  };

  const selectAllPurchaseInvoices = () => {
    setSelectedPurchaseUids(purchaseInvoices.map((invoice) => invoice.uid));
  };

  const clearPurchaseSelection = () => {
    setSelectedPurchaseUids([]);
  };

  const toggleSelectAllPurchaseInvoices = () => {
    if (allPurchaseSelected) {
      clearPurchaseSelection();
      return;
    }
    selectAllPurchaseInvoices();
  };

  const openPurchaseBulkEdit = () => {
    if (selectedPurchaseUids.length === 0) return;
    if (selectedPurchaseUids.length === 1) {
      const invoice = purchaseInvoices.find((row) => row.uid === selectedPurchaseUids[0]);
      const matchedSupplier = invoice?.supplier_name
        ? canonicalSuppliers.find((supplier) => supplier.name === invoice.supplier_name) || null
        : null;
      setPurchaseBulkDraft({
        supplierName: invoice?.supplier_name || '',
        supplierId: matchedSupplier?.id ?? null,
        invoiceNumber: invoice?.invoice_number || '',
        invoiceDate: (invoice?.invoice_date || '').slice(0, 10),
        dueDate: (invoice?.due_date || '').slice(0, 10),
        amount: invoice?.total_amount != null ? String(invoice.total_amount) : '',
        currency: invoice?.currency || '',
        category: invoice?.category || '',
        status: invoice?.status || '',
        description: invoice?.description || '',
      });
    } else {
      setPurchaseBulkDraft(emptyPurchaseBulkDraft());
    }
  };

  const savePurchaseBulkEdit = async () => {
    if (!purchaseBulkDraft || selectedPurchaseUids.length === 0) return;
    const fields = purchaseBulkFieldsPayload(purchaseBulkDraft);
    if (Object.keys(fields).length === 0) {
      setError('Fill at least one field to apply to the selected invoices.');
      return;
    }
    try {
      setPurchaseBulkBusy(true);
      setError(null);
      await api.post('/api/purchase-invoices/bulk-update', {
        uids: selectedPurchaseUids,
        fields,
      });
      setPurchaseBulkDraft(null);
      fetchPurchaseInvoices();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update selected invoices');
    } finally {
      setPurchaseBulkBusy(false);
    }
  };

  const confirmPurchaseBulkDelete = async () => {
    if (selectedPurchaseUids.length === 0) return;
    try {
      setPurchaseBulkBusy(true);
      setError(null);
      await api.post('/api/purchase-invoices/bulk-delete', {
        uids: selectedPurchaseUids,
      });
      setPurchaseBulkDeleteOpen(false);
      setSelectedPurchaseUids([]);
      fetchPurchaseInvoices();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete selected invoices');
    } finally {
      setPurchaseBulkBusy(false);
    }
  };

  const reanalyzePurchaseInvoice = async (uid: string) => {
    const response = await api.post(`/api/purchase-invoices/${uid}/reanalyze`, {}, { timeout: 120000 });
    const updated = response.data?.invoice;
    if (updated?.uid) {
      setPurchaseInvoices((prev) => prev.map((row) => (row.uid === updated.uid ? { ...row, ...updated } : row)));
    }
    return updated;
  };

  const reanalyzePurchaseInvoiceRow = async (uid: string) => {
    try {
      setReanalyzeUid(uid);
      setError(null);
      await reanalyzePurchaseInvoice(uid);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to re-analyse invoice');
    } finally {
      setReanalyzeUid(null);
    }
  };

  const reanalyzeSelectedPurchaseInvoices = async () => {
    const uids = [...selectedPurchaseUids];
    if (uids.length === 0) return;
    try {
      setPurchaseBulkBusy(true);
      setError(null);
      const failures: string[] = [];
      for (let i = 0; i < uids.length; i += 1) {
        setPurchaseReanalyzeProgress(`Re-analysing ${i + 1} of ${uids.length}`);
        try {
          await reanalyzePurchaseInvoice(uids[i]);
        } catch (err: any) {
          failures.push(err.response?.data?.error || uids[i]);
        }
      }
      if (failures.length > 0) {
        setError(`Re-analyse finished with ${failures.length} failure${failures.length === 1 ? '' : 's'}.`);
      }
    } finally {
      setPurchaseReanalyzeProgress(null);
      setPurchaseBulkBusy(false);
      fetchPurchaseInvoices();
    }
  };

  const handleView = (meta: ViewMeta) => {
    setViewMeta(meta);
    setViewDialogOpen(true);
  };

  const previewPendingFile = (item: PendingUpload) => {
    const ext = item.file.name.includes('.') ? item.file.name.split('.').pop() : '';
    handleView({
      type:
        item.documentType === 'sales_invoice'
          ? 'sales-invoice'
          : item.documentType === 'purchase_invoice'
            ? 'purchase-invoice'
            : 'receipt',
      uid: item.localId,
      fileName: item.file.name,
      mimeType: item.file.type || null,
      fileExtension: ext || null,
      localFile: item.file,
    });
  };

  const handleDownload = async (type: string, uid: string, fileName: string) => {
    try {
      const endpoint = fileEndpointFor(type, uid);
      const response = await api.get(endpoint, {
        responseType: 'blob',
        timeout: 180000,
        params: { download: 1 },
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to download file');
    }
  };

  const updatePendingUpload = (localId: string, patch: Partial<PendingUpload>) => {
    setPendingUploads((prev) => prev.map((item) => (item.localId === localId ? { ...item, ...patch } : item)));
  };

  const runExtractForLocalId = async (localId: string) => {
    let item = pendingUploadsRef.current.find((row) => row.localId === localId);
    if (!item) {
      await new Promise((resolve) => window.setTimeout(resolve, 0));
      item = pendingUploadsRef.current.find((row) => row.localId === localId);
    }
    if (!item) return;
    setPendingUploads((prev) => prev.map((row) => (
      row.localId === localId ? { ...row, predictStatus: 'pending' } : row
    )));
    try {
      const formData = new FormData();
      formData.append('file', item.file);
      formData.append('document_type', item.documentType);
      formData.append('supplier_names', JSON.stringify(canonicalSupplierNamesRef.current));
      const response = await api.post('/api/documents/extract-metadata', formData, { timeout: 120000 });
      const predicted = response.data || {};
      setPendingUploads((prev) => prev.map((row) => {
        if (row.localId !== localId) return row;
        return {
          ...row,
          predictStatus: 'done',
          description: row.description || predicted.description || '',
          invoiceNumber: row.invoiceNumber || predicted.invoice_number || '',
          invoiceDate: row.invoiceDate || predicted.invoice_date || '',
          supplierName: row.supplierName || predicted.supplier_name || '',
          supplierId: row.supplierId ?? (() => {
            const parsed = Number(predicted.supplier_id);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
          })(),
          receiptDate: row.receiptDate || predicted.receipt_date || '',
          amount: row.amount || formatExtractedAmount(predicted.total_amount ?? predicted.amount),
          currency: row.currency || String(predicted.currency || '').trim().toUpperCase(),
          dueDate: row.dueDate || predicted.due_date || '',
        };
      }));
    } catch {
      setPendingUploads((prev) => prev.map((row) => (
        row.localId === localId ? { ...row, predictStatus: 'error' } : row
      )));
    }
  };

  const drainExtractQueue = async () => {
    if (extractBusyRef.current) return;
    extractBusyRef.current = true;
    try {
      while (extractQueueRef.current.length > 0) {
        const localId = extractQueueRef.current.shift();
        if (localId) {
          await runExtractForLocalId(localId);
        }
      }
    } finally {
      extractBusyRef.current = false;
      if (extractQueueRef.current.length > 0) {
        void drainExtractQueue();
      }
    }
  };

  const enqueueExtract = (localIds: string[]) => {
    extractQueueRef.current.push(...localIds);
    void drainExtractQueue();
  };

  const openUploadForType = (type: DocumentTypeChoice) => {
    documentTypeInputRef.current = type;
    setDocumentTypeInput(type);
    setUploadDialogOpen(true);
  };

  const addPendingFiles = (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (files.length === 0) return;
    const created = files.map((file) => newPendingUpload(file, documentTypeInputRef.current));
    pendingUploadsRef.current = [...pendingUploadsRef.current, ...created];
    setPendingUploads(pendingUploadsRef.current);
    enqueueExtract(created.map((item) => item.localId));
  };

  const resetUploadDialog = () => {
    extractQueueRef.current = [];
    pendingUploadsRef.current = [];
    setUploadDialogOpen(false);
    setPendingUploads([]);
    setSelectedLocalIds([]);
    setMetadataEditIds([]);
    setBulkDraft(null);
    setUploadProgress(null);
  };

  const toggleSelectedLocalId = (localId: string) => {
    setSelectedLocalIds((prev) => (
      prev.includes(localId) ? prev.filter((id) => id !== localId) : [...prev, localId]
    ));
  };

  const allPendingSelected = pendingUploads.length > 0 && selectedLocalIds.length === pendingUploads.length;

  const toggleSelectAllPending = () => {
    setSelectedLocalIds(allPendingSelected ? [] : pendingUploads.map((item) => item.localId));
  };

  const openMetadataEditor = (localIds: string[]) => {
    if (localIds.length === 0) return;
    setMetadataEditIds(localIds);
    if (localIds.length === 1) {
      setBulkDraft(null);
      return;
    }
    const selectedItems = pendingUploads.filter((item) => localIds.includes(item.localId));
    const types = Array.from(new Set(selectedItems.map((item) => item.documentType)));
    setBulkDraft(emptyMetadataDraft(types.length === 1 ? types[0] : documentTypeInput));
  };

  const closeMetadataEditor = () => {
    setMetadataEditIds([]);
    setBulkDraft(null);
  };

  const applyBulkMetadata = () => {
    if (!bulkDraft || metadataEditIds.length === 0) return;
    const patch = nonEmptyMetadataPatch(bulkDraft);
    setPendingUploads((prev) => prev.map((item) => (
      metadataEditIds.includes(item.localId) ? { ...item, ...patch } : item
    )));
    closeMetadataEditor();
  };

  const removePendingUpload = (localId: string) => {
    setPendingUploads((prev) => prev.filter((row) => row.localId !== localId));
    setSelectedLocalIds((prev) => prev.filter((id) => id !== localId));
  };

  const uploadOnePending = async (item: PendingUpload) => {
    const formData = new FormData();
    formData.append('document_type', item.documentType);
    formData.append('file', item.file);
    if (item.description) formData.append('description', item.description);
    if (item.receiptDate) formData.append('receipt_date', item.receiptDate);
    if (item.invoiceDate) formData.append('invoice_date', item.invoiceDate);
    if (item.invoiceNumber) formData.append('invoice_number', item.invoiceNumber);
    if (item.supplierName) formData.append('supplier_name', item.supplierName);
    if (item.supplierId != null) formData.append('supplier_id', String(item.supplierId));
    if (item.amount) {
      formData.append('amount', item.amount);
      formData.append('total_amount', item.amount);
    }
    if (item.currency) formData.append('currency', item.currency);
    if (item.dueDate) formData.append('due_date', item.dueDate);
    await api.post('/api/documents/upload', formData, {
      timeout: 180000,
    });
  };

  const handleUploadDocument = async () => {
    if (pendingUploads.length === 0) {
      setError('Please select a file to upload');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      for (let i = 0; i < pendingUploads.length; i += 1) {
        setUploadProgress(`Uploading ${i + 1} of ${pendingUploads.length}`);
        await uploadOnePending(pendingUploads[i]);
      }
      resetUploadDialog();
      fetchReceipts();
      fetchSalesInvoices();
      fetchPurchaseInvoices();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload document');
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const openLinkDialogForDocument = (document: UnifiedDocument) => {
    setSelectedDocument(document);
    setLinkDialogOpen(true);
    fetchTxFirstTransactions();
  };

  const linkDocumentToTransaction = async (transactionId: number, document?: UnifiedDocument) => {
    const doc = document || selectedDocument;
    if (!doc) return;
    try {
      setLoading(true);
      await api.post('/api/transaction-documents/link', {
        transaction_id: transactionId,
        document_type: doc.document_type,
        document_uid: doc.uid
      });
      setLinkDialogOpen(false);
      fetchTxFirstTransactions();
      if (doc.document_type === 'receipt') {
        fetchReceipts();
      } else if (doc.document_type === 'purchase_invoice') {
        fetchPurchaseInvoices();
      } else if (doc.document_type === 'sales_invoice') {
        fetchSalesInvoices();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to link document to transaction');
    } finally {
      setLoading(false);
    }
  };

  const closeTxMatchDialog = () => {
    matchQueueRef.current = [];
    matchQueueIndexRef.current = 0;
    matchIncludeAmountOnlyRef.current = false;
    setMatchQueueIndex(0);
    setMatchQueueLength(0);
    setMatchDoc(null);
    setMatchPayload(null);
    setMatchError(null);
    setMatchAcceptingId(null);
    setRejectedTxIds([]);
    setMatchAmountInput('');
    setMatchCurrencyInput('EUR');
    setMatchLoading(false);
    matchOcrAttemptedRef.current = {};
    setMatchOcrBusy(false);
    setMatchPreviewError(null);
    if (matchPreviewUrlRef.current) {
      URL.revokeObjectURL(matchPreviewUrlRef.current);
      matchPreviewUrlRef.current = null;
    }
    setMatchPreviewUrl(null);
  };

  const loadTxMatchDoc = (doc: TxMatchDoc) => {
    setMatchDoc(doc);
    setMatchPayload(null);
    setRejectedTxIds([]);
    setMatchAcceptingId(null);
    setMatchAmountInput('');
    setMatchCurrencyInput('EUR');
    setMatchError(null);
    void fetchTxMatches(doc);
  };

  const advanceMatchQueue = () => {
    const queue = matchQueueRef.current;
    const nextIndex = matchQueueIndexRef.current + 1;
    if (nextIndex >= queue.length) {
      closeTxMatchDialog();
      fetchPurchaseInvoices();
      return;
    }
    matchQueueIndexRef.current = nextIndex;
    setMatchQueueIndex(nextIndex);
    loadTxMatchDoc(queue[nextIndex]);
  };

  const fetchTxMatches = async (doc: TxMatchDoc) => {
    setMatchLoading(true);
    setMatchError(null);
    try {
      const response = await api.post('/api/documents/suggest-tx-matches', {
        document_type: doc.type,
        document_uid: doc.uid,
        include_amount_only: matchIncludeAmountOnlyRef.current,
      });
      setMatchPayload(response.data);
      if (response.data?.amount != null) {
        setMatchAmountInput(String(response.data.amount));
      }
      if (response.data?.currency) {
        setMatchCurrencyInput(String(response.data.currency));
      }
      const missingAmount = response.data?.amount == null;
      if (
        doc.type === 'purchase_invoice'
        && missingAmount
        && !matchOcrAttemptedRef.current[doc.uid]
      ) {
        matchOcrAttemptedRef.current[doc.uid] = true;
        setMatchOcrBusy(true);
        try {
          await reanalyzePurchaseInvoice(doc.uid);
          const retry = await api.post('/api/documents/suggest-tx-matches', {
            document_type: doc.type,
            document_uid: doc.uid,
            include_amount_only: matchIncludeAmountOnlyRef.current,
          });
          setMatchPayload(retry.data);
          if (retry.data?.amount != null) {
            setMatchAmountInput(String(retry.data.amount));
          }
          if (retry.data?.currency) {
            setMatchCurrencyInput(String(retry.data.currency));
          }
          setMatchError(retry.data?.error || null);
        } catch (ocrErr: any) {
          setMatchError(ocrErr.response?.data?.error || 'Could not read amount from this document');
        } finally {
          setMatchOcrBusy(false);
        }
      }
    } catch (err: any) {
      const data = err.response?.data;
      if (data && (data.document_uid || Array.isArray(data.candidates))) {
        setMatchPayload(data);
        const missingAmount = data.amount == null;
        if (
          doc.type === 'purchase_invoice'
          && missingAmount
          && !matchOcrAttemptedRef.current[doc.uid]
        ) {
          matchOcrAttemptedRef.current[doc.uid] = true;
          setMatchOcrBusy(true);
          try {
            await reanalyzePurchaseInvoice(doc.uid);
            const retry = await api.post('/api/documents/suggest-tx-matches', {
              document_type: doc.type,
              document_uid: doc.uid,
              include_amount_only: matchIncludeAmountOnlyRef.current,
            });
            setMatchPayload(retry.data);
            if (retry.data?.amount != null) {
              setMatchAmountInput(String(retry.data.amount));
            }
            if (retry.data?.currency) {
              setMatchCurrencyInput(String(retry.data.currency));
            }
            setMatchError(retry.data?.error || null);
          } catch (ocrErr: any) {
            setMatchError(ocrErr.response?.data?.error || data?.error || 'Could not read amount from this document');
          } finally {
            setMatchOcrBusy(false);
          }
        } else {
          setMatchError(data?.error || 'Failed to find matching transactions');
        }
      } else {
        setMatchPayload(null);
        setMatchError(data?.error || 'Failed to find matching transactions');
      }
    } finally {
      setMatchLoading(false);
    }
  };

  const openTxMatchDialog = (doc: TxMatchDoc) => {
    matchQueueRef.current = [];
    matchQueueIndexRef.current = 0;
    matchIncludeAmountOnlyRef.current = false;
    setMatchQueueIndex(0);
    setMatchQueueLength(0);
    loadTxMatchDoc(doc);
  };

  const openPurchaseBulkTxMatch = () => {
    const docs = selectedPurchaseUids
      .map((uid) => purchaseInvoices.find((invoice) => invoice.uid === uid))
      .filter((invoice): invoice is PurchaseInvoice => Boolean(invoice))
      .map((invoice) => ({
        type: 'purchase_invoice' as const,
        uid: invoice.uid,
        label: invoice.invoice_number || invoice.file_name,
        fileName: invoice.file_name,
        mimeType: invoice.mime_type,
        fileExtension: invoice.file_extension,
      }));
    if (docs.length === 0) return;
    matchIncludeAmountOnlyRef.current = false;
    matchQueueRef.current = docs;
    matchQueueIndexRef.current = 0;
    setMatchQueueIndex(0);
    setMatchQueueLength(docs.length);
    loadTxMatchDoc(docs[0]);
  };

  const openPandleInvoiceReview = async () => {
    try {
      setPandleReviewBusy(true);
      setError(null);
      const response = await api.get('/api/purchase-invoices/pandle-link-queue', {
        params: { kind: 'all' },
      });
      const items = (response.data?.items || []) as Array<{
        uid: string;
        invoice_number?: string;
        file_name?: string;
        mime_type?: string | null;
        file_extension?: string | null;
      }>;
      const docs = items.map((item) => ({
        type: 'purchase_invoice' as const,
        uid: item.uid,
        label: item.invoice_number || item.file_name || item.uid,
        fileName: item.file_name,
        mimeType: item.mime_type,
        fileExtension: item.file_extension,
      }));
      if (docs.length === 0) {
        setError('No Pandle invoices to review.');
        return;
      }
      matchIncludeAmountOnlyRef.current = true;
      matchQueueRef.current = docs;
      matchQueueIndexRef.current = 0;
      setMatchQueueIndex(0);
      setMatchQueueLength(docs.length);
      loadTxMatchDoc(docs[0]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load Pandle invoice queue');
    } finally {
      setPandleReviewBusy(false);
    }
  };

  const saveInvoiceAmountAndRematch = async () => {
    if (!matchDoc || !matchAmountInput.trim()) {
      setMatchError('Enter the invoice amount, then search again.');
      return;
    }
    try {
      setMatchLoading(true);
      setMatchError(null);
      const endpoint = matchDoc.type === 'purchase_invoice'
        ? `/api/purchase-invoices/${matchDoc.uid}`
        : `/api/sales-invoices/${matchDoc.uid}`;
      await api.put(endpoint, {
        amount: matchAmountInput.trim(),
        total_amount: matchAmountInput.trim(),
        currency: matchCurrencyInput.trim().toUpperCase() || 'EUR',
      });
      if (matchDoc.type === 'purchase_invoice') {
        fetchPurchaseInvoices();
      } else {
        fetchSalesInvoices();
      }
      await fetchTxMatches(matchDoc);
    } catch (err: any) {
      setMatchError(err.response?.data?.error || 'Failed to save invoice amount');
      setMatchLoading(false);
    }
  };

  const acceptTxMatch = async (txId: number) => {
    if (!matchDoc) return;
    try {
      setMatchAcceptingId(txId);
      setMatchError(null);
      await api.post('/api/transaction-documents/link', {
        transaction_id: txId,
        document_type: matchDoc.type,
        document_uid: matchDoc.uid,
      });
      const linkedType = matchDoc.type;
      if (matchQueueRef.current.length > 0) {
        advanceMatchQueue();
      } else {
        closeTxMatchDialog();
        if (linkedType === 'purchase_invoice') {
          fetchPurchaseInvoices();
        } else {
          fetchSalesInvoices();
        }
      }
    } catch (err: any) {
      setMatchError(err.response?.data?.error || 'Failed to link transaction');
    } finally {
      setMatchAcceptingId(null);
    }
  };

  const openReceiptMetadata = (receipt: Receipt) => {
    setEditReceipt(receipt);
    setEditReceiptDescription(receipt.description || '');
    setEditReceiptDate((receipt.receipt_date || '').slice(0, 10));
    setEditReceiptSupplierId(receipt.supplier_id ?? null);
  };

  const saveReceiptMetadata = async () => {
    if (!editReceipt) return;
    try {
      setLoading(true);
      await api.put(`/api/receipts/${editReceipt.uid}`, {
        description: editReceiptDescription,
        receipt_date: editReceiptDate || null,
        supplier_id: editReceiptSupplierId,
      });
      setEditReceipt(null);
      fetchReceipts();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update receipt');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const GBP_TO_EUR = 1.16;
  const formatCurrency = (amount: number | null | undefined, currency: string = 'EUR') => {
    if (amount === null || amount === undefined) return '-';
    const base = new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: currency
    }).format(amount);
    if (currency === 'GBP') {
      const eur = new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(amount * GBP_TO_EUR);
      return `${base} (${eur})`;
    }
    return base;
  };

  const visibleMatchCandidates = (matchPayload?.candidates || []).filter((row) => !rejectedTxIds.includes(row.id));
  const missingInvoiceAmount = Boolean(matchError && /amount/i.test(matchError) && matchPayload?.amount == null);

  return (
    <Box sx={{ width: '100%' }}>
      <Card>
        <CardContent>
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="h4">
              Receipts & Invoices
            </Typography>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="receipts and invoices tabs">
              <Tab icon={<PurchaseIcon />} label="Purchase Invoices" iconPosition="start" />
              <Tab icon={<ReceiptIcon />} label="Receipts" iconPosition="start" />
              <Tab icon={<InvoiceIcon />} label="Sales Invoices" iconPosition="start" />
            </Tabs>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Receipts Tab */}
          <TabPanel value={tabValue} index={1}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" justifyContent="space-between">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchReceipts}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openUploadForType('receipt')}
                disabled={loading}
              >
                Upload Receipt(s)
              </Button>
            </Stack>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
                <Table size="small" sx={FIT_TABLE_SX}>
                  <FitColgroup
                    cols={[
                      { key: 'uid_num', pct: 7 },
                      { key: 'receipt_id', pct: 12 },
                      { key: 'linked_tx_id', pct: 11 },
                      { key: 'date', pct: 11 },
                      { key: 'supplier', pct: 14 },
                      { key: 'size', pct: 8 },
                      { key: 'description', pct: 16 },
                      { key: 'actions', pct: 15 },
                    ]}
                  />
                  <TableHead>
                    <TableRow>
                      <TableCell>UID</TableCell>
                      <TableCell>Receipt ID</TableCell>
                      <TableCell>Linked TX ID</TableCell>
                      <TableCell>Receipt Date</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {receipts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No receipts found
                        </TableCell>
                      </TableRow>
                    ) : (
                      receipts.map((receipt) => (
                        <TableRow key={receipt.uid}>
                          <TableCell>{receipt.id ?? '-'}</TableCell>
                          <TableCell>
                            <Box component="span" sx={fitTruncateSx} title={receipt.revolut_receipt_id || receipt.uid}>
                              {receipt.revolut_receipt_id || receipt.uid.substring(0, 8)}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box
                              component="span"
                              sx={fitTruncateSx}
                              title={(receipt.linked_transaction_ids || []).join(', ')}
                            >
                              {(receipt.linked_transaction_ids || []).length > 0
                                ? receipt.linked_transaction_ids!.join(', ')
                                : '-'}
                            </Box>
                          </TableCell>
                          <TableCell>{formatDate(receipt.receipt_date)}</TableCell>
                          <TableCell>
                            <Box component="span" sx={fitTruncateSx} title={receipt.supplier_name || ''}>
                              {receipt.supplier_name || '-'}
                            </Box>
                          </TableCell>
                          <TableCell>{formatFileSize(receipt.file_size_bytes)}</TableCell>
                          <TableCell>
                            <Box component="span" sx={fitTruncateSx} title={receipt.description || ''}>{receipt.description || '-'}</Box>
                          </TableCell>
                          <TableCell align="right" className="fit-table-no-clip">
                            <Tooltip title="Edit metadata">
                              <IconButton
                                size="small"
                                onClick={() => openReceiptMetadata(receipt)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={() => handleView({
                                  type: 'receipt',
                                  uid: receipt.uid,
                                  fileName: receipt.file_name,
                                  mimeType: receipt.mime_type,
                                  fileExtension: receipt.file_extension,
                                })}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton
                                size="small"
                                onClick={() => handleDownload('receipt', receipt.uid, receipt.file_name)}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete('receipt', receipt.uid, receipt.file_name)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Link to TX">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openLinkDialogForDocument({
                                  uid: receipt.uid,
                                  file_name: receipt.file_name,
                                  created_at: receipt.created_at,
                                  document_type: 'receipt'
                                })}
                              >
                                <LinkIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Sales Invoices Tab */}
          <TabPanel value={tabValue} index={2}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" justifyContent="space-between">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchSalesInvoices}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openUploadForType('sales_invoice')}
                disabled={loading}
              >
                Upload Sales Invoice(s)
              </Button>
            </Stack>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
                <Table size="small" sx={FIT_TABLE_SX}>
                  <FitColgroup
                    cols={[
                      { key: 'uid_num', pct: 8 },
                      { key: 'invoice_number', pct: 14 },
                      { key: 'customer' },
                      { key: 'date', pct: 11 },
                      { key: 'due_date', pct: 11 },
                      { key: 'to_pay', pct: 12 },
                      { key: 'amount', pct: 11 },
                      { key: 'status', pct: 10 },
                      { key: 'actions', pct: 18 },
                    ]}
                  />
                  <TableHead>
                    <TableRow>
                      <TableCell>UID</TableCell>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>To pay</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesInvoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          No sales invoices found
                        </TableCell>
                      </TableRow>
                    ) : (
                      salesInvoices.map((invoice) => (
                        <TableRow key={invoice.uid}>
                          <TableCell>{invoice.id ?? '-'}</TableCell>
                          <TableCell>
                            <Box component="span" sx={fitTruncateSx} title={invoice.invoice_number || invoice.file_name}>
                              {invoice.invoice_number || '-'}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box component="span" sx={fitTruncateSx} title={invoice.customer_name || ''}>{invoice.customer_name || '-'}</Box>
                          </TableCell>
                          <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                          <TableCell>{formatDate(invoice.due_date)}</TableCell>
                          <TableCell className="fit-table-no-clip">
                            <DuePayCell dueDate={invoice.due_date} />
                          </TableCell>
                          <TableCell>{formatCurrency(invoice.total_amount, invoice.currency)}</TableCell>
                          <TableCell>
                            <Chip
                              label={invoice.status}
                              color={
                                invoice.status === 'paid' ? 'success' :
                                invoice.status === 'overdue' ? 'error' :
                                invoice.status === 'sent' ? 'info' : 'default'
                              }
                              size="small"
                              sx={fitChipSx}
                            />
                          </TableCell>
                          <TableCell align="right" className="fit-table-no-clip">
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={() => handleView({
                                  type: 'sales-invoice',
                                  uid: invoice.uid,
                                  fileName: invoice.file_name,
                                  mimeType: invoice.mime_type,
                                  fileExtension: invoice.file_extension,
                                })}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton
                                size="small"
                                onClick={() => handleDownload('sales-invoice', invoice.uid, invoice.file_name)}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete('sales-invoice', invoice.uid, invoice.invoice_number || invoice.file_name)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Suggest TX by amount and customer">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => openTxMatchDialog({
                                  type: 'sales_invoice',
                                  uid: invoice.uid,
                                  label: invoice.invoice_number || invoice.file_name,
                                })}
                              >
                                <FindInPageIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Link to TX">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openLinkDialogForDocument({
                                  uid: invoice.uid,
                                  file_name: invoice.file_name,
                                  created_at: invoice.created_at,
                                  document_type: 'sales_invoice'
                                })}
                              >
                                <LinkIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Purchase Invoices Tab */}
          <TabPanel value={tabValue} index={0}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchPurchaseInvoices}
                  disabled={loading || purchaseBulkBusy}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  onClick={allPurchaseSelected ? clearPurchaseSelection : selectAllPurchaseInvoices}
                  disabled={loading || purchaseInvoices.length === 0}
                >
                  {allPurchaseSelected ? 'Clear selection' : 'Select all'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={openPurchaseBulkEdit}
                  disabled={loading || purchaseBulkBusy || selectedPurchaseUids.length === 0}
                >
                  Edit selected{selectedPurchaseUids.length > 0 ? ` (${selectedPurchaseUids.length})` : ''}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ReanalyzeIcon />}
                  onClick={reanalyzeSelectedPurchaseInvoices}
                  disabled={loading || purchaseBulkBusy || selectedPurchaseUids.length === 0}
                >
                  Re-analyse{selectedPurchaseUids.length > 0 ? ` (${selectedPurchaseUids.length})` : ''}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FindInPageIcon />}
                  onClick={openPurchaseBulkTxMatch}
                  disabled={loading || purchaseBulkBusy || pandleReviewBusy || selectedPurchaseUids.length === 0}
                >
                  Suggest TX{selectedPurchaseUids.length > 0 ? ` (${selectedPurchaseUids.length})` : ''}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<FindInPageIcon />}
                  onClick={openPandleInvoiceReview}
                  disabled={loading || purchaseBulkBusy || pandleReviewBusy}
                >
                  {pandleReviewBusy ? 'Loading…' : 'Review Pandle invoices'}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setPurchaseBulkDeleteOpen(true)}
                  disabled={loading || purchaseBulkBusy || selectedPurchaseUids.length === 0}
                >
                  Delete selected{selectedPurchaseUids.length > 0 ? ` (${selectedPurchaseUids.length})` : ''}
                </Button>
              </Stack>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openUploadForType('purchase_invoice')}
                disabled={loading}
              >
                Upload Purchase Invoice(s)
              </Button>
            </Stack>
            {purchaseReanalyzeProgress && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {purchaseReanalyzeProgress}
              </Typography>
            )}

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
                <Table size="small" sx={FIT_TABLE_SX}>
                  <FitColgroup
                    cols={[
                      { key: 'select', pct: 4 },
                      { key: 'uid_num', pct: 7 },
                      { key: 'invoice_number', pct: 11 },
                      { key: 'supplier' },
                      { key: 'date', pct: 9 },
                      { key: 'due_date', pct: 8 },
                      { key: 'to_pay', pct: 10 },
                      { key: 'amount', pct: 8 },
                      { key: 'tx', pct: 10 },
                      { key: 'category', pct: 8 },
                      { key: 'status', pct: 7 },
                      { key: 'actions', pct: 15 },
                    ]}
                  />
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allPurchaseSelected}
                          indeterminate={selectedPurchaseUids.length > 0 && !allPurchaseSelected}
                          onChange={toggleSelectAllPurchaseInvoices}
                          disabled={purchaseInvoices.length === 0}
                          inputProps={{ 'aria-label': 'Select all purchase invoices' }}
                        />
                      </TableCell>
                      <TableCell>UID</TableCell>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>To pay</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>TX amount</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchaseInvoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center">
                          No purchase invoices found
                        </TableCell>
                      </TableRow>
                    ) : (
                      purchaseInvoices.map((invoice) => (
                        <TableRow key={invoice.uid} selected={selectedPurchaseUids.includes(invoice.uid)}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedPurchaseUids.includes(invoice.uid)}
                              onChange={() => togglePurchaseSelected(invoice.uid)}
                              inputProps={{ 'aria-label': `Select invoice ${invoice.invoice_number || invoice.file_name}` }}
                            />
                          </TableCell>
                          <TableCell>{invoice.id ?? '-'}</TableCell>
                          <TableCell>
                            <Box component="span" sx={fitTruncateSx} title={invoice.invoice_number || invoice.file_name}>
                              {invoice.invoice_number || '-'}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box component="span" sx={fitTruncateSx} title={invoice.supplier_name || ''}>{invoice.supplier_name || '-'}</Box>
                          </TableCell>
                          <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                          <TableCell>{formatDate(invoice.due_date)}</TableCell>
                          <TableCell className="fit-table-no-clip">
                            <DuePayCell dueDate={invoice.due_date} />
                          </TableCell>
                          <TableCell>{formatCurrency(invoice.total_amount, invoice.currency)}</TableCell>
                          <TableCell className="fit-table-no-clip">
                            {(invoice.linked_transactions || []).length > 0 ? (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {(invoice.linked_transactions || []).map((tx) => (
                                  <Chip
                                    key={tx.id}
                                    size="small"
                                    color="success"
                                    sx={fitChipSx}
                                    component={RouterLink}
                                    to={tx.revolut_id
                                      ? `/transactions?focus_revolut_id=${encodeURIComponent(tx.revolut_id)}`
                                      : `/transactions`}
                                    clickable
                                    label={tx.amount != null
                                      ? formatCurrency(tx.amount, tx.currency || invoice.currency)
                                      : `TX ${tx.id}`}
                                    title={tx.description || tx.revolut_id || `TX ${tx.id}`}
                                  />
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box component="span" sx={fitTruncateSx} title={invoice.category || ''}>{invoice.category || '-'}</Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={invoice.status}
                              color={
                                invoice.status === 'paid' ? 'success' :
                                invoice.status === 'overdue' ? 'error' :
                                invoice.status === 'approved' ? 'info' : 'default'
                              }
                              size="small"
                              sx={fitChipSx}
                            />
                          </TableCell>
                          <TableCell align="right" className="fit-table-no-clip">
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={() => handleView({
                                  type: 'purchase-invoice',
                                  uid: invoice.uid,
                                  fileName: invoice.file_name,
                                  mimeType: invoice.mime_type,
                                  fileExtension: invoice.file_extension,
                                })}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton
                                size="small"
                                onClick={() => handleDownload('purchase-invoice', invoice.uid, invoice.file_name)}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete('purchase-invoice', invoice.uid, invoice.invoice_number || invoice.file_name)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Re-analyse with AI">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => reanalyzePurchaseInvoiceRow(invoice.uid)}
                                  disabled={purchaseBulkBusy || reanalyzeUid != null}
                                >
                                  {reanalyzeUid === invoice.uid ? <CircularProgress size={16} /> : <ReanalyzeIcon />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Suggest TX by amount and supplier">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => openTxMatchDialog({
                                  type: 'purchase_invoice',
                                  uid: invoice.uid,
                                  label: invoice.invoice_number || invoice.file_name,
                                  fileName: invoice.file_name,
                                  mimeType: invoice.mime_type,
                                  fileExtension: invoice.file_extension,
                                })}
                              >
                                <FindInPageIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Link to TX">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openLinkDialogForDocument({
                                  uid: invoice.uid,
                                  file_name: invoice.file_name,
                                  created_at: invoice.created_at,
                                  document_type: 'purchase_invoice'
                                })}
                              >
                                <LinkIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {itemToDelete?.name}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(purchaseBulkDraft)}
        onClose={() => !purchaseBulkBusy && setPurchaseBulkDraft(null)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle>
          Edit {selectedPurchaseUids.length} purchase invoice{selectedPurchaseUids.length === 1 ? '' : 's'}
        </DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto' }}>
          {purchaseBulkDraft && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {selectedPurchaseUids.length > 1 && (
                <Typography variant="body2" color="text.secondary">
                  Filled fields are copied to every selected invoice. Blank fields are left as they are.
                </Typography>
              )}
              <Autocomplete
                options={canonicalSuppliers}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={
                  canonicalSuppliers.find((supplier) => supplier.id === purchaseBulkDraft.supplierId)
                  || canonicalSuppliers.find((supplier) => supplier.name === purchaseBulkDraft.supplierName)
                  || null
                }
                onChange={(_, value) => setPurchaseBulkDraft((prev) => (prev ? {
                  ...prev,
                  supplierId: value?.id ?? null,
                  supplierName: value?.name ?? '',
                } : prev))}
                autoHighlight
                fullWidth
                slotProps={{
                  popper: {
                    sx: { zIndex: (theme) => theme.zIndex.modal + 10 },
                  },
                }}
                ListboxProps={{ sx: { maxHeight: 280 } }}
                renderInput={(params) => <TextField {...params} label="Supplier" />}
              />
              <TextField
                label="Invoice Number"
                value={purchaseBulkDraft.invoiceNumber}
                onChange={(e) => setPurchaseBulkDraft((prev) => (prev ? { ...prev, invoiceNumber: e.target.value } : prev))}
                fullWidth
              />
              <TextField
                label="Invoice Date (YYYY-MM-DD)"
                value={purchaseBulkDraft.invoiceDate}
                onChange={(e) => setPurchaseBulkDraft((prev) => (prev ? { ...prev, invoiceDate: e.target.value } : prev))}
                fullWidth
              />
              <TextField
                label="Due Date (YYYY-MM-DD)"
                value={purchaseBulkDraft.dueDate}
                onChange={(e) => setPurchaseBulkDraft((prev) => (prev ? { ...prev, dueDate: e.target.value } : prev))}
                fullWidth
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Amount"
                  value={purchaseBulkDraft.amount}
                  onChange={(e) => setPurchaseBulkDraft((prev) => (prev ? { ...prev, amount: e.target.value } : prev))}
                  fullWidth
                />
                <TextField
                  label="Currency"
                  value={purchaseBulkDraft.currency}
                  onChange={(e) => setPurchaseBulkDraft((prev) => (prev ? { ...prev, currency: e.target.value.toUpperCase() } : prev))}
                  fullWidth
                  inputProps={{ maxLength: 3 }}
                />
              </Stack>
              <TextField
                label="Category"
                value={purchaseBulkDraft.category}
                onChange={(e) => setPurchaseBulkDraft((prev) => (prev ? { ...prev, category: e.target.value } : prev))}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={purchaseBulkDraft.status}
                  onChange={(e) => setPurchaseBulkDraft((prev) => (prev ? { ...prev, status: String(e.target.value) } : prev))}
                >
                  {selectedPurchaseUids.length > 1 && <MenuItem value="">No change</MenuItem>}
                  <MenuItem value="pending">pending</MenuItem>
                  <MenuItem value="approved">approved</MenuItem>
                  <MenuItem value="paid">paid</MenuItem>
                  <MenuItem value="overdue">overdue</MenuItem>
                  <MenuItem value="cancelled">cancelled</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Description"
                value={purchaseBulkDraft.description}
                onChange={(e) => setPurchaseBulkDraft((prev) => (prev ? { ...prev, description: e.target.value } : prev))}
                fullWidth
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurchaseBulkDraft(null)} disabled={purchaseBulkBusy}>Cancel</Button>
          <Button variant="contained" onClick={savePurchaseBulkEdit} disabled={purchaseBulkBusy}>
            {purchaseBulkBusy ? 'Saving…' : `Apply to ${selectedPurchaseUids.length}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={purchaseBulkDeleteOpen}
        onClose={() => !purchaseBulkBusy && setPurchaseBulkDeleteOpen(false)}
      >
        <DialogTitle>Delete selected invoices</DialogTitle>
        <DialogContent>
          <Typography>
            Delete {selectedPurchaseUids.length} purchase invoice{selectedPurchaseUids.length === 1 ? '' : 's'} and their files? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurchaseBulkDeleteOpen(false)} disabled={purchaseBulkBusy}>Cancel</Button>
          <Button onClick={confirmPurchaseBulkDelete} color="error" variant="contained" disabled={purchaseBulkBusy}>
            {purchaseBulkBusy ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={uploadDialogOpen}
        onClose={() => !loading && resetUploadDialog()}
        maxWidth={pendingUploads.length > 1 ? 'lg' : 'sm'}
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle>{uploadActionLabel(documentTypeInput)}</DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto' }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" component="label" sx={{ flex: 1, justifyContent: 'flex-start' }}>
                {pendingUploads.length === 0
                  ? 'Choose file(s)'
                  : pendingUploads.length === 1
                    ? pendingUploads[0].file.name
                    : `${pendingUploads.length} files selected — add more`}
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={(e) => {
                    if (e.target.files) addPendingFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </Button>
              {pendingUploads.length === 1 && (
                <Tooltip title="Preview document">
                  <IconButton
                    onClick={() => previewPendingFile(pendingUploads[0])}
                    aria-label="Preview document"
                  >
                    <ViewIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            {pendingUploads.length === 1 && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Metadata is optional — the app will try to read each file and suggest values you can edit.
                </Typography>
                {pendingUploads[0].predictStatus === 'pending' && (
                  <Typography variant="body2" color="text.secondary">Reading document…</Typography>
                )}
                {pendingUploads[0].predictStatus === 'error' && (
                  <Typography variant="body2" color="warning.main">Couldn’t read this file. Fill in details manually, or upload without them.</Typography>
                )}
                <DocumentMetadataFields
                  item={pendingUploads[0]}
                  showType={false}
                  suppliers={canonicalSuppliers}
                  onChange={(patch) => {
                    updatePendingUpload(pendingUploads[0].localId, patch);
                  }}
                />
              </>
            )}
            {pendingUploads.length > 1 && (
              <>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Files are read automatically to suggest metadata. Select rows to edit several at once.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={selectedLocalIds.length === 0}
                    onClick={() => openMetadataEditor(selectedLocalIds)}
                  >
                    Edit selected{selectedLocalIds.length > 0 ? ` (${selectedLocalIds.length})` : ''}
                  </Button>
                </Stack>
                <TableContainer component={Paper} sx={{ ...FIT_TABLE_CONTAINER_SX, maxHeight: '45vh' }}>
                  <Table size="small" sx={FIT_TABLE_SX}>
                    <FitColgroup
                      cols={[
                        { key: 'select', pct: 5 },
                        { key: 'file', pct: 20 },
                        { key: 'type', pct: 12 },
                        { key: 'details' },
                        { key: 'status', pct: 11 },
                        { key: 'actions', pct: 16 },
                      ]}
                    />
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={allPendingSelected}
                            indeterminate={selectedLocalIds.length > 0 && !allPendingSelected}
                            onChange={toggleSelectAllPending}
                            inputProps={{ 'aria-label': 'Select all files' }}
                          />
                        </TableCell>
                        <TableCell>File</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Metadata</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingUploads.map((item) => {
                        const status = pendingStatusChip(item);
                        return (
                        <TableRow key={item.localId}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedLocalIds.includes(item.localId)}
                              onChange={() => toggleSelectedLocalId(item.localId)}
                              inputProps={{ 'aria-label': `Select ${item.file.name}` }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box component="span" sx={fitTruncateSx} title={item.file.name}>
                              {item.file.name}
                            </Box>
                          </TableCell>
                          <TableCell>{documentTypeLabel(item.documentType)}</TableCell>
                          <TableCell className="fit-table-no-clip" sx={{ verticalAlign: 'top', whiteSpace: 'normal' }}>
                            <Stack spacing={0.15}>
                              {pendingMetadataFields(item).map((row) => (
                                <Typography
                                  key={row.label}
                                  variant="caption"
                                  component="div"
                                  sx={{ lineHeight: 1.35, whiteSpace: 'normal', wordBreak: 'break-word' }}
                                >
                                  <Box component="span" sx={{ color: 'text.secondary' }}>{row.label}: </Box>
                                  {row.value}
                                </Typography>
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              sx={fitChipSx}
                              color={status.color}
                              label={status.label}
                            />
                          </TableCell>
                          <TableCell align="right" className="fit-table-no-clip">
                            <Tooltip title="Preview document">
                              <IconButton
                                size="small"
                                onClick={() => previewPendingFile(item)}
                                aria-label={`Preview ${item.file.name}`}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit metadata">
                              <IconButton size="small" onClick={() => openMetadataEditor([item.localId])}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removePendingUpload(item.localId)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            {uploadProgress && (
              <Typography variant="body2" color="text.secondary">
                {uploadProgress}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetUploadDialog} disabled={loading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUploadDocument}
            disabled={loading || pendingUploads.length === 0}
          >
            {pendingUploads.length > 1 ? `Upload ${pendingUploads.length} files` : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={metadataEditIds.length > 0}
        onClose={closeMetadataEditor}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1.5 }}>
          <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
            {metadataEditIds.length > 1
              ? `Edit metadata for ${metadataEditIds.length} files`
              : `Edit metadata${pendingUploads.find((item) => item.localId === metadataEditIds[0]) ? ` — ${pendingUploads.find((item) => item.localId === metadataEditIds[0])?.file.name}` : ''}`}
          </Box>
          {metadataEditIds.length === 1 && pendingUploads.some((item) => item.localId === metadataEditIds[0]) && (
            <Tooltip title="Preview document">
              <IconButton
                onClick={() => previewPendingFile(pendingUploads.find((item) => item.localId === metadataEditIds[0])!)}
                aria-label="Preview document"
              >
                <ViewIcon />
              </IconButton>
            </Tooltip>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto' }}>
          {metadataEditIds.length === 1 && pendingUploads.some((item) => item.localId === metadataEditIds[0]) && (
            <Box sx={{ mt: 1 }}>
              <DocumentMetadataFields
                item={pendingUploads.find((item) => item.localId === metadataEditIds[0])!}
                showType={false}
                suppliers={canonicalSuppliers}
                onChange={(patch) => updatePendingUpload(metadataEditIds[0], patch)}
              />
            </Box>
          )}
          {metadataEditIds.length > 1 && bulkDraft && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Filled fields are copied to every selected file. Blank fields are left as they are.
              </Typography>
              <DocumentMetadataFields
                item={bulkDraft}
                showType={false}
                suppliers={canonicalSuppliers}
                onChange={(patch) => setBulkDraft((prev) => (prev ? { ...prev, ...patch } : prev))}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {metadataEditIds.length > 1 ? (
            <>
              <Button onClick={closeMetadataEditor}>Cancel</Button>
              <Button variant="contained" onClick={applyBulkMetadata}>
                Apply to {metadataEditIds.length} files
              </Button>
            </>
          ) : (
            <Button onClick={closeMetadataEditor} variant="contained">Done</Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editReceipt)}
        onClose={() => setEditReceipt(null)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle>
          Edit receipt{editReceipt?.file_name ? ` — ${editReceipt.file_name}` : ''}
        </DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto' }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Description"
              value={editReceiptDescription}
              onChange={(e) => setEditReceiptDescription(e.target.value)}
              fullWidth
            />
            <TextField
              label="Receipt Date (YYYY-MM-DD)"
              value={editReceiptDate}
              onChange={(e) => setEditReceiptDate(e.target.value)}
              fullWidth
            />
            <Autocomplete
              options={canonicalSuppliers}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={canonicalSuppliers.find((supplier) => supplier.id === editReceiptSupplierId) || null}
              onChange={(_, value) => setEditReceiptSupplierId(value?.id ?? null)}
              autoHighlight
              fullWidth
              slotProps={{
                popper: {
                  sx: { zIndex: (theme) => theme.zIndex.modal + 10 },
                },
              }}
              ListboxProps={{ sx: { maxHeight: 280 } }}
              renderInput={(params) => <TextField {...params} label="Supplier" />}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditReceipt(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveReceiptMetadata} disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Link Document To Transaction</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Selected document: {selectedDocument?.file_name || 'None selected'}
          </Typography>
          <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
            <Table size="small" sx={FIT_TABLE_SX}>
              <FitColgroup
                cols={[
                  { key: 'date', pct: 14 },
                  { key: 'description' },
                  { key: 'amount', pct: 13 },
                  { key: 'account', pct: 18 },
                  { key: 'action', pct: 12 },
                ]}
              />
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {txTabTransactions.map((tx) => (
                  <TableRow key={`link-${tx.id}`}>
                    <TableCell>{formatDate(tx.completed_at)}</TableCell>
                    <TableCell>
                      <Box component="span" sx={fitTruncateSx} title={tx.description || ''}>{tx.description || '-'}</Box>
                    </TableCell>
                    <TableCell>{tx.amount !== null ? formatCurrency(tx.amount, tx.currency || 'EUR') : '-'}</TableCell>
                    <TableCell>
                      <Box component="span" sx={fitTruncateSx} title={tx.bank_account_name || tx.account_source || ''}>
                        {tx.bank_account_name || tx.account_source}
                      </Box>
                    </TableCell>
                    <TableCell align="right" className="fit-table-no-clip">
                      <Button size="small" variant="contained" onClick={() => linkDocumentToTransaction(tx.id)}>
                        Link
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(matchDoc)}
        onClose={() => !matchLoading && !matchAcceptingId && !matchOcrBusy && closeTxMatchDialog()}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle>
          {matchQueueLength > 0 ? `${matchQueueIndex + 1} of ${matchQueueLength} · ` : ''}
          Suggest matching transaction{matchDoc?.label ? ` — ${matchDoc.label}` : ''}
        </DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto' }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 1fr) minmax(320px, 1.2fr)' },
                alignItems: 'start',
              }}
            >
              <Box
                sx={{
                  minHeight: 280,
                  maxHeight: { xs: 360, md: 520 },
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {matchPreviewLoading && <CircularProgress />}
                {!matchPreviewLoading && matchPreviewError && (
                  <Alert severity="warning" sx={{ m: 1 }}>{matchPreviewError}</Alert>
                )}
                {!matchPreviewLoading && !matchPreviewError && matchPreviewUrl && matchDoc && (
                  isPdfPreview(
                    guessPreviewMime(matchDoc.mimeType, matchDoc.fileExtension, matchDoc.fileName),
                    matchDoc.fileExtension,
                    matchDoc.fileName
                  ) ? (
                    <iframe
                      src={matchPreviewUrl}
                      style={{ width: '100%', height: 500, border: 'none' }}
                      title="Invoice preview"
                    />
                  ) : isImagePreview(
                    guessPreviewMime(matchDoc.mimeType, matchDoc.fileExtension, matchDoc.fileName),
                    matchDoc.fileExtension,
                    matchDoc.fileName
                  ) ? (
                    <Box
                      component="img"
                      src={matchPreviewUrl}
                      alt={matchDoc.fileName || 'Invoice'}
                      sx={{ maxWidth: '100%', maxHeight: 500, objectFit: 'contain' }}
                    />
                  ) : (
                    <Typography color="text.secondary" sx={{ p: 2 }}>
                      Preview is not available for this file type.
                    </Typography>
                  )
                )}
              </Box>
              <Stack spacing={2}>
            {matchOcrBusy && (
              <Alert severity="info">Reading amount from this invoice (PDF, JPG, or PNG)…</Alert>
            )}
            {(matchPayload?.linked_transactions || []).length > 0 && (
              <Alert severity="success">
                Linked TX amount{' '}
                {(matchPayload?.linked_transactions || []).map((tx) => (
                  <Chip
                    key={tx.id}
                    size="small"
                    sx={{ ml: 0.5 }}
                    component={RouterLink}
                    to={tx.revolut_id
                      ? `/transactions?focus_revolut_id=${encodeURIComponent(tx.revolut_id)}`
                      : '/transactions'}
                    clickable
                    label={tx.amount != null
                      ? formatCurrency(tx.amount, tx.currency || matchPayload?.currency || 'EUR')
                      : `TX ${tx.id}`}
                  />
                ))}
              </Alert>
            )}
            {matchPayload && (
              <Typography variant="body2" color="text.secondary">
                Looking for {matchDoc?.type === 'sales_invoice' ? 'inflow' : 'outflow'} TXs of{' '}
                {matchPayload.amount != null
                  ? formatCurrency(matchPayload.amount, matchPayload.currency || 'EUR')
                  : 'the invoice amount'}
                {matchPayload.party_name ? ` · ${matchPayload.party_name}` : ''}
                {matchPayload.invoice_date ? ` · invoice ${formatDate(matchPayload.invoice_date)}` : ''}
              </Typography>
            )}
            {matchError && (
              <Alert severity={/amount/i.test(matchError) ? 'warning' : 'error'}>{matchError}</Alert>
            )}
            {missingInvoiceAmount && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                <TextField
                  label="Invoice amount"
                  value={matchAmountInput}
                  onChange={(e) => setMatchAmountInput(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Currency"
                  value={matchCurrencyInput}
                  onChange={(e) => setMatchCurrencyInput(e.target.value.toUpperCase())}
                  inputProps={{ maxLength: 3 }}
                  sx={{ minWidth: 120 }}
                />
                <Button
                  variant="contained"
                  onClick={saveInvoiceAmountAndRematch}
                  disabled={matchLoading || matchOcrBusy || !matchAmountInput.trim()}
                  sx={{ whiteSpace: 'nowrap', mt: { sm: 0.5 } }}
                >
                  Save and search
                </Button>
              </Stack>
            )}
            {matchLoading || matchOcrBusy ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : visibleMatchCandidates.length > 0 ? (
              <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
                <Table size="small" sx={FIT_TABLE_SX}>
                  <FitColgroup
                    cols={[
                      { key: 'date', pct: 14 },
                      { key: 'description' },
                      { key: 'amount', pct: 14 },
                      { key: 'why', pct: 18 },
                      { key: 'action', pct: 22 },
                    ]}
                  />
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Why</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleMatchCandidates.map((tx) => (
                      <TableRow key={`match-${tx.id}`}>
                        <TableCell>{formatDate(tx.completed_at)}</TableCell>
                        <TableCell>
                          <Box component="span" sx={fitTruncateSx} title={tx.description || tx.merchant_name || ''}>
                            {tx.description || tx.merchant_name || '-'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {tx.amount !== null ? formatCurrency(tx.amount, tx.currency || 'EUR') : '-'}
                        </TableCell>
                        <TableCell className="fit-table-no-clip">
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {(tx.reasons || []).map((reason) => (
                              <Chip
                                key={`${tx.id}-${reason}`}
                                size="small"
                                sx={fitChipSx}
                                color={reason === 'Supplier match' ? 'success' : 'default'}
                                label={reason}
                              />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell align="right" className="fit-table-no-clip">
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => acceptTxMatch(tx.id)}
                            disabled={matchAcceptingId != null}
                            sx={{ mr: 0.5 }}
                          >
                            {matchAcceptingId === tx.id ? 'Linking…' : 'Accept'}
                          </Button>
                          <Button
                            size="small"
                            onClick={() => setRejectedTxIds((prev) => (prev.includes(tx.id) ? prev : [...prev, tx.id]))}
                            disabled={matchAcceptingId != null}
                          >
                            Reject
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : !missingInvoiceAmount ? (
              <Alert severity="info">No TX found</Alert>
            ) : null}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTxMatchDialog} disabled={matchLoading || matchOcrBusy || matchAcceptingId != null}>
            {matchQueueLength > 0 ? 'Stop' : 'Close'}
          </Button>
          {matchQueueLength > 0 && (
            <Button
              onClick={advanceMatchQueue}
              disabled={matchLoading || matchOcrBusy || matchAcceptingId != null}
              variant={visibleMatchCandidates.length === 0 && !missingInvoiceAmount ? 'contained' : 'outlined'}
            >
              {matchQueueIndex + 1 < matchQueueLength ? 'Skip' : 'Finish'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* View File Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={closeViewDialog}
        maxWidth="lg"
        fullWidth
        disableEnforceFocus
        PaperProps={{ sx: { height: '90vh' } }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <DialogTitle>
          {viewMeta?.localFile
            ? `Preview${viewMeta.fileName ? ` — ${viewMeta.fileName}` : ''}`
            : `${viewMeta?.type === 'receipt' ? 'Receipt' :
               viewMeta?.type === 'sales-invoice' ? 'Sales Invoice' :
               'Purchase Invoice'}${viewMeta?.fileName ? ` — ${viewMeta.fileName}` : ''}`}
        </DialogTitle>
        <DialogContent
          sx={{
            height: '75vh',
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {previewLoading && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          )}
          {!previewLoading && previewError && (
            <Alert severity="error" sx={{ m: 2 }}>
              {previewError}
            </Alert>
          )}
          {!previewLoading && !previewError && previewUrl && viewMeta && (
            isPdfPreview(
              guessPreviewMime(viewMeta.mimeType, viewMeta.fileExtension, viewMeta.fileName),
              viewMeta.fileExtension,
              viewMeta.fileName
            ) ? (
              <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                <iframe
                  src={previewUrl}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="File Viewer"
                />
              </Box>
            ) : isImagePreview(
              guessPreviewMime(viewMeta.mimeType, viewMeta.fileExtension, viewMeta.fileName),
              viewMeta.fileExtension,
              viewMeta.fileName
            ) ? (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}>
                <Box
                  component="img"
                  src={previewUrl}
                  alt={viewMeta.fileName || 'Document'}
                  sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', p: 2 }}
                />
              </Box>
            ) : (
              <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ flex: 1, p: 4 }}>
                <Typography color="text.secondary">
                  Preview is not available for this file type. Download it to open locally.
                </Typography>
                {viewMeta.fileName && !viewMeta.localFile && (
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(viewMeta.type, viewMeta.uid, viewMeta.fileName || 'document')}
                  >
                    Download
                  </Button>
                )}
              </Stack>
            )
          )}
        </DialogContent>
        <DialogActions>
          {previewUrl && (
            <Button
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}
            >
              Open in new tab
            </Button>
          )}
          {viewMeta?.fileName && !viewMeta.localFile && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(viewMeta.type, viewMeta.uid, viewMeta.fileName || 'document')}
            >
              Download
            </Button>
          )}
          <Button onClick={closeViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReceiptsInvoices;

